import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import EnrollmentService from "../../../_services/EnrollmentService";
import { todayLocalISO } from "./format";

interface Props {
  show: boolean;
  onHide: () => void;
  enrollmentId: number;
  onSuccess: () => void;
}

const AddCreditModal: React.FC<Props> = ({ show, onHide, enrollmentId, onSuccess }) => {
  const [creditTypes, setCreditTypes] = useState<any[]>([]);
  const [creditTypeId, setCreditTypeId] = useState<number | null>(null);
  const [amount, setAmount] = useState("");
  const [transactionDate, setTransactionDate] = useState(todayLocalISO());
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // After a successful save we keep the modal open to show the confirmation;
  // the parent's onSuccess (which refreshes the ledger + closes) fires when the
  // user closes. Closing before success is a plain dismiss.
  const handleClose = () => {
    if (successMsg) onSuccess();
    else onHide();
  };

  useEffect(() => {
    if (show) {
      setError(null);
      setSuccessMsg(null);
      setAmount("");
      setRemarks("");
      setCreditTypeId(null);
      setTransactionDate(todayLocalISO());

      EnrollmentService.getCreditTypes()
        .then((res: any) => {
          if (res.data?.success) setCreditTypes(res.data.data || []);
          else setError(res.data?.message || "Failed to load credit types");
        })
        .catch((err: any) =>
          setError(
            err.response?.data?.message ||
              err.message ||
              "Failed to load credit types",
          ),
        );
    }
  }, [show]);

  const handleSubmit = async () => {
    setError(null);
    const numAmount = Number(amount);
    if (!numAmount || numAmount <= 0) {
      setError("Amount must be greater than 0");
      return;
    }
    if (!creditTypeId) {
      setError("Select a credit type");
      return;
    }

    setSubmitting(true);
    try {
      const res = await EnrollmentService.addCredit(enrollmentId, {
        amount: numAmount,
        creditTypeId,
        transactionDate,
        remarks,
      });
      if (res.data?.success) {
        setSuccessMsg(`Credit of ${numAmount.toLocaleString()} added successfully`);
      } else {
        setError(res.data?.message || "Failed to add credit");
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || "Failed to add credit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>Add Credit</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ fontSize: 13 }}>
        {error && <Alert variant="danger" style={{ fontSize: 12 }}>{error}</Alert>}
        {successMsg && <Alert variant="success" style={{ fontSize: 12 }}>{successMsg}</Alert>}

        <Form.Group className="mb-2">
          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Credit Type</Form.Label>
          <Form.Select
            size="sm"
            value={creditTypeId || ""}
            onChange={(e) => setCreditTypeId(Number(e.target.value))}
          >
            <option value="">-- Select --</option>
            {creditTypes.map((ct: any) => (
              <option key={ct.id} value={ct.id}>{ct.name}</option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Amount</Form.Label>
          <Form.Control
            type="number"
            size="sm"
            min={1}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Transaction Date</Form.Label>
          <Form.Control
            type="date"
            size="sm"
            value={transactionDate}
            onChange={(e) => setTransactionDate(e.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label style={{ fontSize: 12 }}>Remarks</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            size="sm"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        {successMsg ? (
          <Button variant="secondary" size="sm" onClick={handleClose}>Close</Button>
        ) : (
          <>
            <Button variant="secondary" size="sm" onClick={onHide}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={handleSubmit} disabled={submitting}>
              {submitting ? <><Spinner role="status" animation="border" size="sm" /> Saving...</> : "Add Credit"}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default AddCreditModal;
